package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.DraftRepository
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.DateTimeUtils
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StockCountFormScreen(
    supplier: Supplier,
    existingCountId: String? = null,
    onBack: () -> Unit,
    onSaved: () -> Unit,
    onShowHistory: (String) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val focusManager = LocalFocusManager.current
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val draftRepository = remember { DraftRepository() }

    var items by remember { mutableStateOf<List<Item>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }

    // Map to store previous count item details: itemId -> StockCountItem
    val previousCountItems = remember { mutableStateMapOf<String, StockCountItem>() }
    // Map to store delivered quantities: itemId -> Double
    val deliveredQuantities = remember { mutableStateMapOf<String, Double>() }

    // Maps to store inputs: itemId -> value
    val boxesInput = remember { mutableStateMapOf<String, String>() }
    val individualsInput = remember { mutableStateMapOf<String, String>() }
    val percentageInput = remember { mutableStateMapOf<String, String>() }

    var countTitleDate by remember { mutableStateOf<String?>(null) }
    var showPrintDialog by remember { mutableStateOf(false) }
    var navigateAfterPrint by remember { mutableStateOf(false) }

    fun calculateRealAmount(item: Item): Double {
        val boxes = boxesInput[item.id]?.toDoubleOrNull() ?: 0.0
        val individuals = individualsInput[item.id]?.toDoubleOrNull() ?: 0.0
        val percentage = percentageInput[item.id]?.toDoubleOrNull() ?: 0.0

        return when (item.unitMeasureType.lowercase().trim()) {
            "p" -> boxes + (percentage / 100.0)
            "kg", "l" -> boxes
            "kgp", "kgs" -> {
                val packs = boxesInput[item.id]?.toDoubleOrNull() ?: 0.0
                val ind = individualsInput[item.id]?.toDoubleOrNull() ?: 0.0
                val measure = if (item.measurePerPack > 0) item.measurePerPack else 1.0
                (packs * measure) + ind
            }
            "wp" -> {
                val packsPerBox = if (item.packsPerBox > 0) item.packsPerBox else 1.0
                (boxes * packsPerBox) + individuals
            }
            "unit", "u" -> {
                val packsPerBox = if (item.packsPerBox > 0) item.packsPerBox else 1.0
                boxes + (individuals / packsPerBox) + ((percentage / 100.0) / packsPerBox)
            }
            "w", "s" -> boxes
            else -> boxes + (percentage / 100.0)
        }
    }

    fun saveToDraft() {
        if (existingCountId == null && items.isNotEmpty()) {
            val draftItems = items.map { item ->
                StockCountItem(
                    itemId = item.id,
                    countedBoxes = boxesInput[item.id]?.toDoubleOrNull() ?: 0.0,
                    countedIndividuals = individualsInput[item.id]?.toDoubleOrNull() ?: 0.0,
                    countedPercentage = percentageInput[item.id]?.toDoubleOrNull() ?: 0.0,
                    countedRealAmount = calculateRealAmount(item)
                )
            }
            draftRepository.saveDraft(supplier.id, draftItems)
        }
    }

    LaunchedEffect(supplier.id) {
        isLoading = true
        // 1. Fetch Items
        val itemsResult = repository.getItems(supplier.id)
        if (itemsResult.isSuccess) {
            items = itemsResult.getOrThrow()

            // 2. Fetch Previous Count details and received orders between last count and now
            repository.getStockCounts(supplier.id).onSuccess { counts ->
                existingCountId?.let { id ->
                    counts.firstOrNull { it.id == id }?.let { count ->
                        countTitleDate = DateTimeUtils.formatTitleDate(count.timestamp)
                    }
                }
                // Filter out the one we might be currently editing if existingCountId is set
                val lastCompleteCount = counts.firstOrNull { it.id != existingCountId }
                lastCompleteCount?.let { count ->
                    repository.getStockCountDetails(count.id).onSuccess { details ->
                        details.forEach { detail ->
                            previousCountItems[detail.itemId] = detail
                        }
                    }
                }

                repository.getOrders(supplier.id).onSuccess { orders ->
                    val lastCountTimestamp = lastCompleteCount?.timestamp
                    val receivedOrders = orders.filter { order ->
                        val isReceived = order.status.equals("Received", ignoreCase = true)
                        val receivedAt = order.receivedAt
                        if (!isReceived || receivedAt == null) return@filter false

                        if (lastCountTimestamp != null) {
                            receivedAt >= lastCountTimestamp
                        } else {
                            true
                        }
                    }

                    receivedOrders.forEach { order ->
                        repository.getOrderItems(order.id).onSuccess { orderItems ->
                            orderItems.forEach { orderItem ->
                                val current = deliveredQuantities[orderItem.itemId] ?: 0.0
                                deliveredQuantities[orderItem.itemId] = current + (orderItem.qtyReceived ?: 0.0)
                            }
                        }
                    }
                }
            }

            // 3. Load data for the form: Amending > Draft > Empty
            if (existingCountId != null) {
                repository.getStockCountDetails(existingCountId).onSuccess { details ->
                    details.forEach { detail ->
                        boxesInput[detail.itemId] = detail.countedBoxes?.let { if (it == 0.0) "" else formatWhole(it) } ?: ""
                        individualsInput[detail.itemId] = detail.countedIndividuals?.let { if (it == 0.0) "" else formatWhole(it) } ?: ""
                        percentageInput[detail.itemId] = detail.countedPercentage?.let { if (it == 0.0) "" else formatWhole(it) } ?: ""
                    }
                }
            } else {
                val draft = draftRepository.loadDraft(supplier.id)
                if (draft != null) {
                    draft.forEach { detail ->
                        boxesInput[detail.itemId] = detail.countedBoxes?.let { if (it == 0.0) "" else formatWhole(it) } ?: ""
                        individualsInput[detail.itemId] = detail.countedIndividuals?.let { if (it == 0.0) "" else formatWhole(it) } ?: ""
                        percentageInput[detail.itemId] = detail.countedPercentage?.let { if (it == 0.0) "" else formatWhole(it) } ?: ""
                    }
                } else {
                    items.forEach { 
                        boxesInput[it.id] = ""
                        individualsInput[it.id] = ""
                        percentageInput[it.id] = ""
                    }
                }
            }
        }
        isLoading = false
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            snackbarHost = { SnackbarHost(snackbarHostState) },
            topBar = {
                TopAppBar(
                    title = {
                        Text(
                            if (existingCountId == null) "New Count"
                            else countTitleDate?.let { "Amend $it" } ?: "Amend Count"
                        )
                    },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    },
                    actions = {
                        val isAdmin = LocalUserRole.current == UserRole.ADMIN
                        OverflowActionsMenu(
                            actions = buildList {
                                if (isAdmin) {
                                    add(OverflowAction("Recalculate") {
                                        scope.launch {
                                            items.forEach { item ->
                                                saveToDraft()
                                            }
                                            snackbarHostState.showSnackbar("Stock count recalculated")
                                        }
                                    })
                                }
                                add(OverflowAction("History") { onShowHistory(supplier.id) })
                                if (!isLoading) {
                                    add(OverflowAction("Print") { showPrintDialog = true })
                                    add(OverflowAction("Save") {
                                        val countItems = items.map { item ->
                                            StockCountItem(
                                                itemId = item.id,
                                                countedBoxes = boxesInput[item.id]?.toDoubleOrNull() ?: 0.0,
                                                countedIndividuals = individualsInput[item.id]?.toDoubleOrNull() ?: 0.0,
                                                countedPercentage = percentageInput[item.id]?.toDoubleOrNull() ?: 0.0,
                                                countedRealAmount = calculateRealAmount(item)
                                            )
                                        }
                                        scope.launch {
                                            val result = if (existingCountId == null) {
                                                repository.createStockCount(CreateStockCountRequest(supplierId = supplier.id, items = countItems))
                                            } else {
                                                repository.editStockCount(EditStockCountRequest(id = existingCountId, items = countItems))
                                            }
                                            result.fold(
                                                onSuccess = {
                                                    if (it.error == null) {
                                                        if (existingCountId == null) draftRepository.clearDraft(supplier.id)
                                                        navigateAfterPrint = true
                                                        showPrintDialog = true
                                                    } else {
                                                        snackbarHostState.showSnackbar("Error: ${it.error}")
                                                    }
                                                },
                                                onFailure = { snackbarHostState.showSnackbar("Network error: ${it.message}") }
                                            )
                                        }
                                    })
                                }
                            }
                        )
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (!isLoading && items.isEmpty()) {
                    Text("No items found for this supplier.", modifier = Modifier.align(Alignment.Center))
                } else if (!isLoading) {
                    Column(modifier = Modifier.fillMaxSize()) {
                        // Header
                        Row(
                            modifier = Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.primary).padding(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text("Item", modifier = Modifier.weight(1f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold)
                            Text("Count", modifier = Modifier.wrapContentWidth(), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold, textAlign = TextAlign.End)
                        }

                        LazyColumn(modifier = Modifier.weight(1f)) {
                            items(items) { item ->
                                val prevDetail = previousCountItems[item.id]
                                val delivered = deliveredQuantities[item.id] ?: 0.0
                                val unit = item.unitMeasureType.lowercase().trim()

                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 10.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column(modifier = Modifier.weight(1f).padding(end = 8.dp)) {
                                        Text(
                                            item.name,
                                            fontWeight = FontWeight.Bold,
                                            maxLines = 2,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                        Text("${item.packDescription} (${item.unitMeasureType})", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant, maxLines = 1, overflow = TextOverflow.Ellipsis)
                                        if (prevDetail != null) {
                                            Text("Prev: ${formatStockAmount(item, prevDetail.countedRealAmount)}", style = MaterialTheme.typography.bodySmall)
                                        }
                                        if (delivered > 0.0) {
                                            Text("Deliv: +${formatStockAmount(item, delivered)}", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.secondary)
                                        }
                                    }

                                    Row(
                                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        when {
                                            unit == "kgp" || unit == "kgs" -> {
                                                NumberStepper(
                                                    value = boxesInput[item.id] ?: "",
                                                    onValueChange = { boxesInput[item.id] = it },
                                                    label = "Packs",
                                                    stacked = true
                                                )
                                                FreeTextAmountField(
                                                    value = individualsInput[item.id] ?: "",
                                                    onValueChange = { individualsInput[item.id] = it },
                                                    label = "Kg"
                                                )
                                            }
                                            unit == "wp" || unit == "unit" || unit == "u" -> {
                                                NumberStepper(
                                                    value = boxesInput[item.id] ?: "",
                                                    onValueChange = { boxesInput[item.id] = it },
                                                    label = "Box",
                                                    stacked = true
                                                )
                                                NumberStepper(
                                                    value = individualsInput[item.id] ?: "",
                                                    onValueChange = { individualsInput[item.id] = it },
                                                    label = "Ind",
                                                    stacked = true
                                                )
                                                if (unit == "unit" || unit == "u") {
                                                    NumberStepper(
                                                        value = percentageInput[item.id] ?: "",
                                                        onValueChange = { percentageInput[item.id] = it },
                                                        label = "%",
                                                        step = 10.0,
                                                        max = 100.0,
                                                        stacked = true
                                                    )
                                                }
                                            }
                                            unit == "p" -> {
                                                NumberStepper(
                                                    value = boxesInput[item.id] ?: "",
                                                    onValueChange = { boxesInput[item.id] = it },
                                                    label = "Box",
                                                    stacked = true
                                                )
                                                NumberStepper(
                                                    value = percentageInput[item.id] ?: "",
                                                    onValueChange = { percentageInput[item.id] = it },
                                                    label = "%",
                                                    step = 10.0,
                                                    max = 100.0,
                                                    stacked = true
                                                )
                                            }
                                            unit == "kg" || unit == "l" -> {
                                                FreeTextAmountField(
                                                    value = boxesInput[item.id] ?: "",
                                                    onValueChange = { boxesInput[item.id] = it },
                                                    label = if (unit == "l") "L" else "Kg"
                                                )
                                            }
                                            else -> {
                                                NumberStepper(
                                                    value = boxesInput[item.id] ?: "",
                                                    onValueChange = { boxesInput[item.id] = it },
                                                    label = "Count",
                                                    stacked = true
                                                )
                                            }
                                        }
                                    }
                                }
                                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                            }
                        }
                    }
                }
            }
        }

        if (showPrintDialog) {
            StockCountPrintDialog(
                supplierName = supplier.name,
                items = items,
                previousCounts = previousCountItems.mapValues { it.value.countedRealAmount },
                deliveredQuantities = deliveredQuantities.toMap(),
                newCounts = items.associate { it.id to calculateRealAmount(it) },
                onDismiss = {
                    showPrintDialog = false
                    if (navigateAfterPrint) {
                        navigateAfterPrint = false
                        onSaved()
                    }
                }
            )
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }
    }
}
